/*
 * Bounding flight for the small birds (art/Birds.js). Worked out in body lengths (L) and
 * seconds, so a nearer, bigger bird just covers more pixels and every bird obeys the
 * same gravity.
 *
 * Like a finch, a bird beats its wings in short bursts that lift it and drive it forward,
 * then folds them and coasts on a falling arc under gravity while air drag bleeds off a
 * little speed; the next burst wins it back. That gives the gently undulating path of a
 * real small bird. Wing work follows need: longer bursts and shorter coasts to climb,
 * the reverse to come down, and steady flapping through a turn.
 */

// Sprite strip (src/images/royal-mahal/bird-flight.webp): frames 0..13 are one wingbeat
// starting on the upstroke, frame 14 is the wings folded for coasting.
export const FRAMES = 15;
export const CYCLE = 14;
export const TUCK = 14;
const RECOVERY = 12 / CYCLE; // wings coming back up: bursts start and end here

// Unhurried flight: the whole bird runs at this pace, so speeds and wingbeats scale by it,
// gravity by its square and timings by its inverse. The path keeps its shape, only slower.
const PACE = 0.62;

const G = 5 * PACE * PACE; // gravity, L/s²
const DRAG_FLAP = 0.24 * PACE; // horizontal drag at cruise speed, wings out (1/s)
const DRAG_TUCK = 0.1 * PACE; // ...and wings folded
const VDRAG_FLAP = 1.2 * PACE; // damping of vertical speed (1/s)
const VDRAG_TUCK = 0.35 * PACE;
const TURN_PUSH = 2.4 * PACE; // how hard a bird hauls itself round, in cruise speeds per second

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const rand = (lo, hi) => lo + Math.random() * (hi - lo);

/** A bird with its own cruise speed and wingbeat. */
export const createBird = (L) => ({
  L,
  U: rand(5, 6.2) * PACE, // cruise speed, L/s
  f0: rand(4.2, 5.1) * PACE, // wingbeats per second
  x: 0,
  y: 0,
  vx: 0,
  vy: 0,
  dir: 1,
  turning: false,
  turnAt: null,
  mode: "flap",
  phase: 0,
  beats: 3,
  coast: 0.5,
  lift: 0.7,
  timer: 0,
  yTarget: 0,
  retarget: 4,
  pitch: 0,
});

// A burst of wingbeats sized to what the bird needs: e > 0 when it wants to climb.
const startBurst = (b, e) => {
  b.mode = "flap";
  b.phase = Math.floor(b.phase) + RECOVERY;
  b.beats = clamp(Math.round(3 + e + rand(-0.5, 0.5)), 2, 5);
  b.coast = clamp(0.48 - 0.2 * e + rand(-0.1, 0.1), 0.22, 0.8) / PACE;
  // Now and then, with height to lose, a longer coast: a shallow swoop
  if (e < 0.2 && Math.random() < 0.12) b.coast = rand(0.9, 1.25) / PACE;
  // Lift over the burst that balances the drop of the coast to follow, plus the climb wanted
  b.lift = (b.coast / (b.beats / b.f0)) * (1 + 0.6 * e) + 0.04;
};

/** Puts the bird in its stretch of sky (at `x`, `y` if given), flying at cruise speed. */
export const launch = (b, area, { dir = Math.random() < 0.5 ? 1 : -1, x, y } = {}) => {
  const { w, h } = area;
  b.dir = dir;
  b.x = x !== undefined ? x : rand(0.15, 0.85) * w;
  b.y = y !== undefined ? clamp(y, 0.25 * h, 0.85 * h) : rand(0.3, 0.75) * h;
  b.yTarget = clamp(b.y + rand(-0.2, 0.2) * h, 0.3 * h, 0.75 * h);
  b.retarget = rand(3, 7) / PACE;
  b.vx = dir * b.U * b.L * rand(0.92, 1.05);
  b.pitch = 0;
  b.turning = false;
  // Now and then a bird turns back partway across
  b.turnAt = Math.random() < 0.25 ? (dir > 0 ? rand(0.35, 0.7) : rand(0.3, 0.65)) * w : null;
  b.phase = Math.random();
  startBurst(b, 0);
  // Arrive as in steady flight: at the foot of a wave, still sinking from the last coast
  b.vy = ((G * b.coast) / 2) * b.L;
};

/** Advances one bird by dt seconds. */
export const step = (b, dt, area) => {
  const { w, h } = area;
  const { L, U } = b;

  // The height it is making for drifts now and then, so it climbs and sinks over the scene
  b.retarget -= dt;
  if (b.retarget <= 0) {
    b.yTarget = clamp(b.yTarget + rand(-0.35, 0.35) * h, 0.3 * h, 0.75 * h);
    b.retarget = rand(3, 7) / PACE;
  }
  const vxL = b.vx / L;
  const vyL = b.vy / L;
  // > 0 when below that height (or sinking away from it): it needs to work harder.
  // Near the edges of its stretch of sky it is steered firmly back in.
  // Firmly kept off the top of its stretch of sky (the text sits above it), gently off the foot.
  // (A bird that is leaving ignores the limits and just climbs away.)
  const edge = b.exiting ? 0 : 4 * Math.max(0, 0.25 * h - b.y) - Math.max(0, b.y - 0.85 * h);
  const e = clamp(((b.y - b.yTarget - edge) / L + vyL) / 3, -1, 1);

  // Near the edge of the view it swings round rather than flying out of sight
  const edgeGap = 0.04 * w + 1.4 * L;
  const nearEdge = b.dir > 0 ? b.x > w - edgeGap : b.x < edgeGap;
  if (!b.turning && !b.exiting && (nearEdge || (b.turnAt !== null && (b.dir > 0 ? b.x >= b.turnAt : b.x <= b.turnAt)))) {
    b.dir = -b.dir;
    b.turning = true;
    // ...and sometimes it will turn again partway back
    b.turnAt = Math.random() < 0.2 ? (b.dir > 0 ? rand(0.45, 0.75) : rand(0.25, 0.55)) * w : null;
    if (b.mode !== "flap") startBurst(b, e);
  }
  if (b.turning && b.dir * vxL > 0.85 * U) b.turning = false;

  // Wings: count beats past the recovery point, then fold and coast
  if (b.mode === "flap") {
    const f = b.f0 * (1 + 0.15 * Math.max(0, e) + (b.turning ? 0.15 : 0));
    const before = b.phase;
    b.phase += f * dt;
    if (Math.floor(before - RECOVERY) !== Math.floor(b.phase - RECOVERY)) {
      b.beats -= 1;
      // High enough and still rising: no need for the rest of the burst
      if (!b.turning && e < -0.5 && vyL < 0) b.beats = 0;
      if (b.beats <= 0) {
        if (b.turning) b.beats = 1;
        else {
          b.mode = "tuck";
          b.timer = b.coast;
        }
      }
    }
  } else {
    b.timer -= dt;
    // Sinking too far below where it wants to be cuts the coast short
    if (b.timer <= 0 || (e > 0.6 && vyL > 0)) startBurst(b, e);
  }

  // Forces, in L/s²
  const flapping = b.mode === "flap";
  let ax;
  let ay;
  if (flapping) {
    // Thrust holds cruise speed against drag, pushing harder when slow; a turn hauls it round
    const thrust = b.turning
      ? TURN_PUSH * U
      : DRAG_FLAP * U * (1 + 2 * clamp(1 - (b.dir * vxL) / U, -0.5, 1));
    ax = b.dir * thrust - (DRAG_FLAP * vxL * Math.abs(vxL)) / U;
    // Each wingbeat is pushed a little harder or softer as the bird finds its height;
    // through a turn it flaps steadily, holding height.
    const lift = b.turning ? clamp(0.1 + 0.6 * e, -0.3, 0.8) : clamp(b.lift + 0.5 * e, -0.2, 1.6);
    ay = -G * lift - VDRAG_FLAP * vyL;
  } else {
    ax = -(DRAG_TUCK * vxL * Math.abs(vxL)) / U;
    ay = G - VDRAG_TUCK * vyL; // a free, falling arc
  }
  b.vx += ax * L * dt;
  b.vy += ay * L * dt;
  b.x += b.vx * dt;
  b.y += b.vy * dt;

  // The body follows the path, with a little lag
  const target = clamp(Math.atan2(b.vy, Math.abs(b.vx)) * 0.6, -0.3, 0.3);
  b.pitch += (target - b.pitch) * (1 - Math.exp(-dt / (0.15 / PACE)));

  return false;
};

/** Which strip frame to show. */
export const frameOf = (b) => (b.mode === "flap" ? Math.floor((((b.phase % 1) + 1) % 1) * CYCLE) : TUCK);

/**
 * Horizontal scale: mirrored when flying left, and briefly narrower while it swings
 * round, as the turning bird is seen end-on.
 */
export const facingOf = (b) => {
  const s = Math.sign(b.vx) || b.dir;
  return s * (0.45 + 0.55 * clamp(Math.abs(b.vx / b.L) / (0.6 * b.U), 0, 1));
};
